import { Component, ElementRef, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { BehaviorSubject, Subscription } from 'rxjs';
import firebase from 'firebase/app';
import 'firebase/firestore';

import { AuthService } from '../../services/auth.service';
import { DicomUtilitiesService } from '../../services/dicom-utilities.service';
import { FilesService } from '../../services/files.service';
import { UIMessagingService } from '../../services/uimessaging.service';
import { StudyRecord, FileRecord } from '../../services/dicomdir-parser';
import { environment } from '../../../environments/environment';

const MAX_CONCURRENT = 3;
const MAX_RETRIES = 3;
const DEFAULT_BUCKET = environment.config.storageBucket;
const BW_TEST_SIZE = 512 * 1024; // 512 KB

interface FileWithPath {
  file: File;
  relativePath: string;
}

interface FileProgress {
  file: File;
  relativePath: string;
  progress: number;
  status: 'pending' | 'uploading' | 'done' | 'failed';
  errorMessage?: string;
}

interface ImagingCaseOption {
  id: string;
  label: string;
  status: string;
}

type UploadStatus = 'idle' | 'scanning' | 'ready' | 'uploading' | 'done' | 'error';
type BandwidthStatus = 'checking' | 'ok' | 'warn' | 'slow';
type CaseMode = 'new' | 'existing';

@Component({
  selector: 'app-add-medical-disk',
  templateUrl: './add-medical-disk.component.html',
  styleUrls: ['./add-medical-disk.component.scss'],
})
export class AddMedicalDiskComponent implements OnInit, OnDestroy {
  @ViewChild('folderInput') folderInput: ElementRef<HTMLInputElement>;

  uploadStatus$ = new BehaviorSubject<UploadStatus>('idle');
  overallProgress$ = new BehaviorSubject<number>(0);
  studies: StudyRecord[] = [];
  fileProgresses: FileProgress[] = [];
  canDropFolder = typeof DataTransferItem.prototype.webkitGetAsEntry === 'function';

  // Case selection
  caseMode: CaseMode = 'new';
  newCaseLabel = '';
  selectedCaseId: string | null = null;
  existingCases: ImagingCaseOption[] = [];

  // Bandwidth
  bandwidthStatus: BandwidthStatus = 'checking';
  bandwidthMbps: number | null = null;
  estimatedMinutes: number | null = null;

  private firmId: string;
  private uploadedBy: string;
  private allFilesWithPaths: FileWithPath[] = [];
  private filteredFilesWithPaths: FileWithPath[] = [];
  private dicomdirRelativePath: string | null = null;
  private casesLoaded = false;
  private userDataSub: Subscription;

  constructor(
    private auth_$: AuthService,
    private dicom_$: DicomUtilitiesService,
    private files_$: FilesService,
    private ui_$: UIMessagingService,
    private ngZone: NgZone,
  ) {}

  ngOnInit() {
    this.auth_$.preventRedirections = true;
    this.userDataSub = this.auth_$.userData.subscribe((data: any) => {
      if (data?.uid && !this.casesLoaded) {
        this.casesLoaded = true;
        this.uploadedBy = data.uid;
        this.firmId = data.ownerID || data.uid;
        this.runBandwidthCheck();
        this.loadExistingCases();
      }
    });
  }

  ngOnDestroy() {
    this.userDataSub?.unsubscribe();
  }

  get totalFileCount(): number {
    return this.filteredFilesWithPaths.length;
  }

  get doneCount(): number {
    return this.fileProgresses.filter(fp => fp.status === 'done').length;
  }

  get failedCount(): number {
    return this.fileProgresses.filter(fp => fp.status === 'failed').length;
  }

  get dropZoneActive(): boolean {
    if (!this.canDropFolder) return false;
    if (this.uploadStatus$.value !== 'idle') return false;
    return this.caseMode === 'new'
      ? this.newCaseLabel.trim().length > 0
      : this.selectedCaseId !== null;
  }

  get currentCaseLabel(): string {
    if (this.caseMode === 'new') return this.newCaseLabel.trim();
    return this.existingCases.find(c => c.id === this.selectedCaseId)?.label || '';
  }

  onCaseModeChange() {
    this.selectedCaseId = null;
  }

  onDragEnter(ev: DragEvent) { ev.preventDefault(); }
  onDragOver(ev: DragEvent) { ev.preventDefault(); }

  async onDrop(ev: DragEvent) {
    ev.preventDefault();
    if (!this.dropZoneActive) return;
    this.uploadStatus$.next('scanning');
    try {
      this.allFilesWithPaths = await this.collectFilesWithPaths(ev.dataTransfer.items);
      await this.processDicomdir();
    } catch (err) {
      console.error('Drop processing error:', err);
      this.ui_$.toastMessage('Failed to scan dropped files.', 'OK');
      this.uploadStatus$.next('error');
    }
  }

  onDropZoneClick() {
    if (!this.dropZoneActive) return;
    this.folderInput.nativeElement.value = '';
    this.folderInput.nativeElement.click();
  }

  async onFolderSelected(event: Event) {
    if (!this.dropZoneActive) return;
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;
    this.uploadStatus$.next('scanning');
    try {
      this.allFilesWithPaths = Array.from(input.files).map(file => ({
        file,
        relativePath: (file as any).webkitRelativePath || file.name,
      }));
      await this.processDicomdir();
    } catch (err) {
      console.error('Folder selection error:', err);
      this.ui_$.toastMessage('Failed to process selected folder.', 'OK');
      this.uploadStatus$.next('error');
    }
  }

  reset() {
    this.uploadStatus$.next('idle');
    this.studies = [];
    this.fileProgresses = [];
    this.allFilesWithPaths = [];
    this.filteredFilesWithPaths = [];
    this.dicomdirRelativePath = null;
    this.overallProgress$.next(0);
    this.estimatedMinutes = null;
  }

  async startUpload() {
    if (!this.firmId) {
      this.ui_$.toastMessage('Not authenticated.', 'OK');
      return;
    }
    const uploadSessionId = this.generateSessionId();

    let imagingCaseId: string;
    if (this.caseMode === 'new') {
      imagingCaseId = await this.createImagingCase(uploadSessionId);
    } else {
      imagingCaseId = this.selectedCaseId;
      await this.addSessionToCase(imagingCaseId, uploadSessionId);
    }

    this.uploadStatus$.next('uploading');
    this.overallProgress$.next(0);

    this.fileProgresses = this.filteredFilesWithPaths.map(fp => ({
      file: fp.file,
      relativePath: fp.relativePath,
      progress: 0,
      status: 'pending' as const,
    }));

    await this.uploadFilesWithConcurrencyControl(uploadSessionId);
    await this.saveFirestoreRecord(uploadSessionId, imagingCaseId);
    this.uploadStatus$.next('done');
  }

  private async runBandwidthCheck() {
    if (!this.firmId) return;
    const testPath = `dicom-staging/${this.firmId}/bandwidth-test/${Date.now()}.tmp`;
    try {
      const { signedUrl } = await this.files_$.getDicomUploadSignedUrl(
        testPath, 'application/octet-stream', DEFAULT_BUCKET,
      );
      const blob = new Blob([new ArrayBuffer(BW_TEST_SIZE)]);
      const start = Date.now();
      await this.xhrPut(signedUrl, blob, 'application/octet-stream', () => {});
      const seconds = (Date.now() - start) / 1000;
      const mbps = (BW_TEST_SIZE * 8) / (seconds * 1_000_000);
      this.ngZone.run(() => {
        this.bandwidthMbps = Math.round(mbps * 10) / 10;
        this.bandwidthStatus = mbps >= 2 ? 'ok' : mbps >= 0.5 ? 'warn' : 'slow';
      });
    } catch (err) {
      console.warn('Bandwidth check failed:', err);
      this.ngZone.run(() => { this.bandwidthStatus = 'warn'; });
    }
  }

  private async loadExistingCases() {
    if (!this.firmId) return;
    try {
      const snap = await firebase.firestore()
        .collection('imagingCases')
        .where('firmId', '==', this.firmId)
        .orderBy('createdAt', 'desc')
        .get();
      this.ngZone.run(() => {
        this.existingCases = snap.docs.map(d => ({
          id: d.id,
          label: d.data().label,
          status: d.data().status,
        }));
      });
    } catch (err) {
      console.error('loadExistingCases error:', err);
    }
  }

  private async processDicomdir() {
    const dicomdirEntry = this.allFilesWithPaths.find(
      fp => fp.file.name.toUpperCase() === 'DICOMDIR',
    );
    if (!dicomdirEntry) {
      this.ui_$.toastMessage('No DICOMDIR file found. Is this a DICOM disk?', 'OK');
      this.uploadStatus$.next('idle');
      return;
    }

    this.dicomdirRelativePath = dicomdirEntry.relativePath;

    let filesData: FileRecord[] = [];
    try {
      let metadata: { studiesData: StudyRecord[]; filesData: FileRecord[] };
      try {
        metadata = await this.dicom_$.getDicomdirMetadataFromBackend(dicomdirEntry.file);
      } catch (backendErr) {
        console.warn('Backend parse failed, falling back to local parse:', backendErr);
        metadata = await this.dicom_$.getDicomdirMetadata(dicomdirEntry.file);
      }
      this.studies = metadata.studiesData || [];
      filesData = metadata.filesData || [];
    } catch (err) {
      console.error('DICOMDIR parse error:', err);
      this.studies = [];
      filesData = [];
    }

    this.filteredFilesWithPaths = this.filterByDicomdir(this.allFilesWithPaths, filesData);
    this.updateEstimatedTime();
    this.uploadStatus$.next('ready');
  }

  private filterByDicomdir(allFiles: FileWithPath[], filesData: FileRecord[]): FileWithPath[] {
    if (filesData.length === 0) {
      console.warn('DICOMDIR returned no file entries — uploading all files as fallback');
      return allFiles;
    }
    const referencedSet = new Set(filesData.map(f => f.ReferencedFileID.toLowerCase()));
    return allFiles.filter(fp => {
      if (fp.file.name.toUpperCase() === 'DICOMDIR') return true;
      const rel = fp.relativePath.toLowerCase();
      if (referencedSet.has(rel)) return true;
      return [...referencedSet].some(r => rel.endsWith('/' + r) || rel === r);
    });
  }

  private updateEstimatedTime() {
    if (!this.bandwidthMbps || this.bandwidthMbps <= 0) return;
    const totalBytes = this.filteredFilesWithPaths.reduce((sum, fp) => sum + fp.file.size, 0);
    const totalMbits = (totalBytes * 8) / 1_000_000;
    this.estimatedMinutes = Math.ceil(totalMbits / this.bandwidthMbps);
  }

  private async createImagingCase(uploadSessionId: string): Promise<string> {
    const ref = await firebase.firestore().collection('imagingCases').add({
      firmId: this.firmId,
      createdBy: this.uploadedBy,
      label: this.newCaseLabel.trim(),
      clioMatterId: null,
      clioMatterName: null,
      patientCaseName: null,
      status: 'pending',
      uploadSessionIds: [uploadSessionId],
      createdAt: firebase.firestore.FieldValue.serverTimestamp(),
      updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
    });
    return ref.id;
  }

  private async addSessionToCase(caseId: string, uploadSessionId: string): Promise<void> {
    await firebase.firestore().collection('imagingCases').doc(caseId).update({
      uploadSessionIds: firebase.firestore.FieldValue.arrayUnion(uploadSessionId),
      updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
    });
  }

  private async uploadFilesWithConcurrencyControl(uploadSessionId: string): Promise<void> {
    const total = this.fileProgresses.length;
    if (total === 0) return;
    let nextIdx = 0;
    let completedCount = 0;
    const runWorker = async (): Promise<void> => {
      while (nextIdx < total) {
        const i = nextIdx++;
        await this.uploadSingleFile(this.fileProgresses[i], uploadSessionId);
        completedCount++;
        this.ngZone.run(() => {
          this.overallProgress$.next(Math.round((completedCount / total) * 100));
        });
      }
    };
    await Promise.all(
      Array.from({ length: Math.min(MAX_CONCURRENT, total) }, () => runWorker()),
    );
  }

  private async uploadSingleFile(fp: FileProgress, uploadSessionId: string, attempt = 1): Promise<void> {
    this.ngZone.run(() => { fp.status = 'uploading'; });
    const gcsPath = `dicom-staging/${this.firmId}/${uploadSessionId}/${fp.relativePath}`;
    const contentType = fp.file.type || 'application/octet-stream';
    try {
      const { signedUrl } = await this.files_$.getDicomUploadSignedUrl(gcsPath, contentType, DEFAULT_BUCKET);
      await this.xhrPut(signedUrl, fp.file, contentType, progress => {
        this.ngZone.run(() => { fp.progress = progress; });
      });
      this.ngZone.run(() => { fp.progress = 100; fp.status = 'done'; });
    } catch (err) {
      if (attempt < MAX_RETRIES) {
        await new Promise(res => setTimeout(res, Math.pow(2, attempt) * 500));
        return this.uploadSingleFile(fp, uploadSessionId, attempt + 1);
      }
      const msg = err?.message || err?.code || String(err);
      this.ngZone.run(() => { fp.status = 'failed'; fp.errorMessage = msg; });
      console.error(`Upload failed for ${fp.relativePath}:`, err);
    }
  }

  private xhrPut(
    url: string,
    file: File | Blob,
    contentType: string,
    onProgress: (pct: number) => void,
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', url);
      xhr.setRequestHeader('Content-Type', contentType);
      xhr.upload.onprogress = ev => {
        if (ev.lengthComputable) onProgress(Math.round((ev.loaded / ev.total) * 100));
      };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) resolve();
        else reject(new Error(`HTTP ${xhr.status}`));
      };
      xhr.onerror = () => reject(new Error('XHR network error'));
      xhr.send(file);
    });
  }

  private async saveFirestoreRecord(uploadSessionId: string, imagingCaseId: string) {
    const failedFiles = this.fileProgresses
      .filter(fp => fp.status === 'failed')
      .map(fp => fp.relativePath);
    const gsUriBase = `gs://${DEFAULT_BUCKET}/dicom-staging/${this.firmId}/${uploadSessionId}`;
    await firebase.firestore().collection('dicomStagingUploads').add({
      firmId: this.firmId,
      uploadedBy: this.uploadedBy,
      imagingCaseId,
      uploadSessionId,
      gsUriBase,
      bucket: DEFAULT_BUCKET,
      dicomdirRelativePath: this.dicomdirRelativePath,
      studies: this.studies,
      fileCount: this.fileProgresses.length,
      failedFiles,
      status: 'awaiting_submit',
      createdAt: firebase.firestore.FieldValue.serverTimestamp(),
      updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
    });
  }

  private generateSessionId(length = 20): string {
    const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
    return Array.from({ length }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
  }

  private async collectFilesWithPaths(items: DataTransferItemList): Promise<FileWithPath[]> {
    const result: FileWithPath[] = [];
    const queue: any[] = [];
    for (let i = 0; i < items.length; i++) {
      const entry = (items[i] as any).webkitGetAsEntry?.();
      if (entry) queue.push(entry);
    }
    while (queue.length > 0) {
      const entry = queue.shift();
      if (entry.isFile) {
        const file = await this.getFileFromEntry(entry);
        if (file) {
          const relativePath = entry.fullPath.replace(/^\//, '');
          result.push({ file, relativePath });
        }
      } else if (entry.isDirectory) {
        const reader = entry.createReader();
        const children = await this.readAllEntries(reader);
        queue.push(...children);
      }
    }
    return result;
  }

  private getFileFromEntry(entry: any): Promise<File> {
    return new Promise((resolve, reject) => entry.file(resolve, reject));
  }

  private async readAllEntries(reader: any): Promise<any[]> {
    const entries: any[] = [];
    let batch = await this.readEntriesPromise(reader);
    while (batch.length > 0) {
      entries.push(...batch);
      batch = await this.readEntriesPromise(reader);
    }
    return entries;
  }

  private readEntriesPromise(reader: any): Promise<any[]> {
    return new Promise((resolve, reject) => reader.readEntries(resolve, reject));
  }
}
